import { api, apiUtils } from './api';

export interface User {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  profile_picture?: string | null;
  is_admin: boolean;
  is_approved: boolean;
  is_teacher?: boolean;  // Set to true for users who can broadcast live sessions
  last_ip?: string;
  created_at?: string;
  updated_at?: string;
}


interface LoginResponse {
  success: boolean;
  message?: string;
  token: string;
  user: User;
  needsPhoneVerification?: boolean;
}

interface CurrentUserResponse {
  success: boolean;
  message?: string;
  user: User;
}

export const authService = {
  // Login user
  async login(credentials: { email: string; password: string }): Promise<{ user: User; token: string; needsPhoneVerification?: boolean }> {
    try {
      console.log('🔐 Attempting login with:', { email: credentials.email, passwordLength: credentials.password.length });
      
      
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      
      if (response && response.token && response.user) {
        // Store token and user data
        apiUtils.setAuthToken(response.token);
        apiUtils.setUserData(response.user);
        
        // Store phone verification status
        if (response.needsPhoneVerification) {
          sessionStorage.setItem('needsPhoneVerification', 'true');
        } else {
          sessionStorage.removeItem('needsPhoneVerification');
        }
        
        console.log('👤 Login successful for user:', response.user.name, 'needsPhone:', response.needsPhoneVerification);
        return {
          user: response.user,
          token: response.token,
          needsPhoneVerification: response.needsPhoneVerification
        };
      }
      
      console.error('❌ Login failed - invalid response structure:', response);
      throw new Error('Login failed');
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint to invalidate session and set ban
      await api.post('/auth/logout', {});
      console.log('👋 Logout API call successful');
    } catch (error) {
      console.error('❌ Logout API error (continuing with local logout):', error);
    } finally {
      // Always clear local auth data even if API call fails
      apiUtils.removeAuthToken();
      console.log('👋 User logged out locally');
    }
  },

  // Get current user info – always verifies with server so is_teacher / is_admin stay fresh
  async getCurrentUser(): Promise<User | null> {
    try {
      console.log('🔍 Verifying session with server...');
      const response = await api.get<CurrentUserResponse>('/auth/verify');

      if (response && response.success && response.user) {
        // Update the local cache with fresh data (picks up is_teacher changes)
        apiUtils.setUserData(response.user);
        console.log('👤 Session verified, user:', response.user.name, 'is_teacher:', response.user.is_teacher);
        return response.user;
      }

      return null;
    } catch (error) {
      console.warn('⚠️ Could not verify session with server, using cache:', error);
      // Fallback to cached data on network error only
      const cachedUser = apiUtils.getUserData();
      return cachedUser ? (cachedUser as User) : null;
    }
  },


  // Register user
  async register(userData: { name: string; email: string; phone?: string; password: string }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post<{ success: boolean; message: string }>('/auth/register', userData);
      return {
        success: response.success,
        message: response.message || 'Registration successful'
      };
    } catch (error) {
      console.error('❌ Registration error:', error);
      throw error;
    }
  }
};